/**
 * GazeTrackingFeature - Frontend feature for eye gaze overlay
 *
 * Receives gaze data from desktop via DataChannel and provides
 * state management for the gaze overlay component.
 */
import { BaseFeature } from '../../core/base-feature.js';

export class GazeTrackingFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Gaze state
        this.isStreaming = false;
        this.isAvailable = false;
        this.lastGaze = null;

        // Callbacks for UI updates
        this.gazeCallbacks = new Set();
        this.statusCallbacks = new Set();

        // Pending request/response tracking
        this.pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'gaze-tracking',
            name: 'Gaze Tracking',
            version: 'v1.0',
            capabilities: ['gaze_overlay'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[GazeTracking] Initializing frontend feature...');
        console.log('[GazeTracking] Core API available:', !!this.core);
    }

    /**
     * Handle incoming messages from desktop
     */
    async handleMessage(message) {
        const { action, payload, messageId, originalMessageId } = message;

        // Check if this is a response to a pending request
        const origId = originalMessageId || payload?.originalMessageId;
        if (origId && this.pendingRequests.has(origId)) {
            const { resolve } = this.pendingRequests.get(origId);
            this.pendingRequests.delete(origId);
            resolve(payload);
            return;
        }

        switch (action) {
            case 'gaze_data':
                this.handleGazeData(payload);
                return;

            default:
                console.log('[GazeTracking] Unknown action:', action);
        }
    }

    /**
     * Send a request and wait for response
     */
    async sendRequest(cmd, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.core.sendFeatureMessage({
                feature: 'gaze-tracking',
                version: 'v1.0',
                cmd,
                payload
            });

            // Store pending request
            this.pendingRequests.set(messageId, { resolve, reject });

            // Timeout after 5 seconds
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error('Request timeout'));
                }
            }, 5000);
        });
    }

    /**
     * Handle incoming gaze data
     */
    handleGazeData(data) {
        this.lastGaze = data;

        // Notify all registered callbacks
        for (const callback of this.gazeCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[GazeTracking] Callback error:', error);
            }
        }
    }

    /**
     * Subscribe to gaze data updates
     * @param {Function} callback - Called with gaze data { x, y, normalizedX, normalizedY, valid }
     * @returns {Function} Unsubscribe function
     */
    subscribeToGaze(callback) {
        this.gazeCallbacks.add(callback);
        return () => this.gazeCallbacks.delete(callback);
    }

    /**
     * Subscribe to status updates
     * @param {Function} callback - Called with { available, streaming }
     * @returns {Function} Unsubscribe function
     */
    subscribeToStatus(callback) {
        this.statusCallbacks.add(callback);
        return () => this.statusCallbacks.delete(callback);
    }

    /**
     * Notify status callbacks
     */
    notifyStatusChange() {
        const status = {
            available: this.isAvailable,
            streaming: this.isStreaming
        };
        for (const callback of this.statusCallbacks) {
            try {
                callback(status);
            } catch (error) {
                console.error('[GazeTracking] Status callback error:', error);
            }
        }
    }

    /**
     * Start gaze streaming
     */
    async startStreaming() {
        console.log('[GazeTracking] Starting streaming...');

        try {
            const response = await this.sendRequest('start_streaming');

            if (response?.success) {
                this.isStreaming = true;
                this.notifyStatusChange();
                console.log('[GazeTracking] Streaming started');
            } else {
                console.error('[GazeTracking] Failed to start:', response?.error);
            }

            return response;
        } catch (error) {
            console.error('[GazeTracking] Error starting streaming:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Stop gaze streaming
     */
    async stopStreaming() {
        console.log('[GazeTracking] Stopping streaming...');

        try {
            const response = await this.sendRequest('stop_streaming');

            if (response?.success) {
                this.isStreaming = false;
                this.lastGaze = null;
                this.notifyStatusChange();
                console.log('[GazeTracking] Streaming stopped');
            }

            return response;
        } catch (error) {
            console.error('[GazeTracking] Error stopping streaming:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get current status from desktop
     */
    async getStatus() {
        try {
            const response = await this.sendRequest('get_status');
            console.log('[GazeTracking] getStatus response:', response);

            if (response?.success) {
                this.isAvailable = response.available;
                this.isStreaming = response.streaming;
                this.notifyStatusChange();
            }

            return response;
        } catch (error) {
            console.error('[GazeTracking] Error getting status:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get last known gaze position
     */
    getLastGaze() {
        return this.lastGaze;
    }

    /**
     * Check if gaze is available
     */
    getIsAvailable() {
        return this.isAvailable;
    }

    /**
     * Check if streaming is active
     */
    getIsStreaming() {
        return this.isStreaming;
    }

    async cleanup() {
        if (this.isStreaming) {
            await this.stopStreaming();
        }
        this.gazeCallbacks.clear();
        this.statusCallbacks.clear();
    }
}
